/**
 * Local Search Storage
 * Stores search history in localStorage for non-authenticated users
 * Cleared upon authentication (login/register)
 */

const SEARCH_STORAGE_KEY = "local_search_history";
const MAX_SEARCHES = 10;

export interface LocalSearchItem {
  id: number;
  search_term: string;
  timestamp: number;
}

export const searchStorage = {
  /**
   * Get all local searches
   */
  getSearches: (): LocalSearchItem[] => {
    if (typeof window === "undefined") return [];
    
    try {
      const stored = localStorage.getItem(SEARCH_STORAGE_KEY);
      if (!stored) return [];
      
      const searches = JSON.parse(stored) as LocalSearchItem[];
      return searches.sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      console.error("[SearchStorage] Error getting searches:", error);
      return [];
    }
  },

  /**
   * Add a new search to local storage
   */
  addSearch: (searchTerm: string): void => {
    if (typeof window === "undefined") return;
    
    try {
      const searches = searchStorage.getSearches();
      
      // Check if already exists
      const existingIndex = searches.findIndex(
        (s) => s.search_term.toLowerCase() === searchTerm.toLowerCase()
      );
      
      if (existingIndex !== -1) {
        // Move to top by updating timestamp
        searches[existingIndex].timestamp = Date.now();
      } else {
        // Add new search
        const newSearch: LocalSearchItem = {
          id: Date.now(),
          search_term: searchTerm,
          timestamp: Date.now(),
        };
        
        searches.unshift(newSearch);
      }
      
      // Keep only MAX_SEARCHES items
      const trimmed = searches.slice(0, MAX_SEARCHES);
      
      localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(trimmed));
    } catch (error) {
      console.error("[SearchStorage] Error adding search:", error);
    }
  },

  /**
   * Remove a specific search
   */
  removeSearch: (id: number): void => {
    if (typeof window === "undefined") return;
    
    try {
      const searches = searchStorage.getSearches();
      const filtered = searches.filter((s) => s.id !== id);
      
      localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error("[SearchStorage] Error removing search:", error);
    }
  },

  /**
   * Clear all local searches
   * Called when user logs in or registers
   */
  clearSearches: (): void => {
    if (typeof window === "undefined") return;
    
    try {
      localStorage.removeItem(SEARCH_STORAGE_KEY);
      console.log("[SearchStorage] Local searches cleared");
    } catch (error) {
      console.error("[SearchStorage] Error clearing searches:", error);
    }
  },

  /**
   * Get search count
   */
  getCount: (): number => {
    return searchStorage.getSearches().length;
  },
};